import Box from '@material-ui/core/Box';
import { makeStyles } from '@material-ui/core/styles';
import clsx from 'clsx';
import React, { useEffect, useRef, useState } from 'react';
import { HospitalInfo } from '../../../kiosk/HospitalInfo';
import PanelStatusWrapper from '../../../Panel/PanelStatusWrapper';
import { useStaffMonitoring } from '@/hooks/useStaffMonitoring';
// @ts-ignore
import BottomPanelStatus from './BottomPanelStatus';
// @ts-ignore
import { Room } from '@/components/pages/kiosk/monitor/monitor.types';
import { useScreenConfiguration } from '@/contexts/ScreenConfigurationContext';
import PanelsStatus from '@/components/Panel/PanelsStatus';

interface PostOp {
  id: string;
  name: string;
  // Add other postOp properties as needed
}

interface MonitorContentProps {
  operationRooms: Room[];
  waitingRoom?: any;
  preOp?: any;
  pacu?: any;
  postOps?: PostOp[];
  hasPostop?: boolean;
  preOpFocused?: boolean;
  pacuFocused?: boolean;
  hasPreOpPriorityModule?: boolean;
  hasNoteModule?: boolean;
  isMini?: boolean;
  isSuperAdmin?: boolean;
  isLiaison?: boolean;
  messagingView?: boolean;
  scope?: any;
  staffMode?: boolean;
  showScheduledProcedureDuration?: boolean;
  showLatePreOpIndicator?: boolean;
  showStaffTracking?: boolean;
  isKiosk?: boolean;
}

export const MonitorContent: React.FC<MonitorContentProps> = ({
  operationRooms,
  waitingRoom,
  preOp,
  pacu,
  postOps,
  hasPreOpPriorityModule,
  hasNoteModule,
  isMini,
  isSuperAdmin,
  isLiaison,
  scope,
  staffMode,
  showStaffTracking,
  hasPostop,
  showLatePreOpIndicator,
  isKiosk,
}) => {
  const hasOtherStatuses = waitingRoom || preOp || pacu || (postOps && Array.isArray(postOps) && postOps.length > 0);
  const classes = useStyles();
  const topRowRef = useRef<HTMLDivElement | null>(null);
  const [topRowHeight, setTopRowHeight] = useState(0);

  const { pacuFocused, preOpFocused, messagingScreen } = useScreenConfiguration();

  const { getStaffForRoom } = useStaffMonitoring(showStaffTracking || false);

  const messagingView = messagingScreen || isLiaison;

  useEffect(() => {
    if (!topRowRef.current) {
      setTopRowHeight(0);
      return;
    }

    const observer = new ResizeObserver(entries => {
      const { height } = entries[0].contentRect;
      setTopRowHeight(height);
    });

    observer.observe(topRowRef.current);
    setTopRowHeight(topRowRef.current.getBoundingClientRect().height);

    return () => observer.disconnect();
  }, [operationRooms.length]);

  return (
    <Box
      className={clsx(classes.root, isKiosk ? classes.rootKiosk : classes.rootScrollable, {
        [classes.focused]: preOpFocused || pacuFocused,
      })}
    >
      <HospitalInfo scope={scope} />

      {operationRooms.length > 0 && !hasOtherStatuses ? (
        <PanelsStatus ref={topRowRef} $miniView={isMini} $wrap={true}>
          {operationRooms.map(operationRoom => (
            <PanelStatusWrapper
              key={operationRoom.id}
              operatingRoom={operationRoom}
              operationRooms={operationRooms}
              isPreOp={!!preOp}
              isSuperAdmin={isSuperAdmin}
              isLiaison={isLiaison}
              staffMode={staffMode}
              getStaffForRoom={getStaffForRoom}
              hasOtherStatuses={hasOtherStatuses}
            />
          ))}
        </PanelsStatus>
      ) : operationRooms.length === 0 && hasOtherStatuses ? (
        <BottomPanelStatus
          waitingRoom={waitingRoom}
          preOp={preOp}
          pacu={pacu}
          postOps={postOps}
          hasPostop={hasPostop}
          preOpFocused={preOpFocused}
          pacuFocused={pacuFocused}
          hasPreOpPriorityModule={hasPreOpPriorityModule}
          hasNoteModule={hasNoteModule}
          isMini={isMini}
          messagingView={messagingView}
          carouselSize={operationRooms.length === 0 ? 9 : 5}
          staffMode={staffMode}
          showLatePreOpIndicator={showLatePreOpIndicator}
          isSuperAdmin={isSuperAdmin}
          getStaffForRoom={getStaffForRoom}
          showStaffTracking={showStaffTracking}
          topRowHeight={topRowHeight}
        />
      ) : (
        <>
          <PanelsStatus ref={topRowRef} $miniView={isMini} $wrap={true} $shrink={messagingView && hasOtherStatuses}>
            {operationRooms.map(operationRoom => (
              <PanelStatusWrapper
                key={operationRoom.id}
                operatingRoom={operationRoom}
                operationRooms={operationRooms}
                isPreOp={preOp}
                isSuperAdmin={isSuperAdmin}
                isLiaison={isLiaison}
                staffMode={staffMode}
                getStaffForRoom={getStaffForRoom}
                hasOtherStatuses={hasOtherStatuses}
              />
            ))}
          </PanelsStatus>
          <BottomPanelStatus
            waitingRoom={waitingRoom}
            preOp={preOp}
            pacu={pacu}
            postOps={postOps}
            hasPostop={hasPostop}
            preOpFocused={preOpFocused}
            pacuFocused={pacuFocused}
            hasPreOpPriorityModule={hasPreOpPriorityModule}
            hasNoteModule={hasNoteModule}
            isMini={isMini}
            messagingView={messagingView || isLiaison}
            staffMode={staffMode}
            showLatePreOpIndicator={showLatePreOpIndicator}
            isSuperAdmin={isSuperAdmin}
            getStaffForRoom={getStaffForRoom}
            showStaffTracking={showStaffTracking}
            topRowHeight={topRowHeight}
          />
        </>
      )}
    </Box>
  );
};

const useStyles = makeStyles(_theme => ({
  root: {
    display: 'flex',
    flexFlow: 'column',
    width: '100vw',
    padding: '0.5rem 1rem 1rem',
  },
  rootKiosk: {
    height: '100vh',
    overflow: 'hidden',
  },
  rootScrollable: {
    minHeight: '100vh',
    overflowY: 'auto' as const,
    '@media (max-width: 767px)': {
      height: 'auto',
      '& > div': {
        flex: '0 0 auto',
      },
      '& > div > div': {
        flex: '0 0 auto',
      },
    },
  },
  focused: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr)',
    gridTemplateRows: '1fr 5fr 12fr',
  },
}));

export default MonitorContent;
